/**
 * useWakeLock Hook
 *
 * React hook for managing the Screen Wake Lock API to prevent device sleep
 * during browser recording sessions. Handles browser compatibility, automatic
 * re-acquisition on visibility change, and graceful degradation.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import type { WakeLockState } from "@/types/browser-recording";

/**
 * Check if Wake Lock API is supported
 */
function isWakeLockSupported(): boolean {
  return "wakeLock" in navigator;
}

/**
 * Hook for managing screen wake lock
 *
 * @returns Wake lock state and control functions
 */
export function useWakeLock() {
  const [state, setState] = useState<WakeLockState>({
    isSupported: isWakeLockSupported(),
    isActive: false,
    error: null,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wakeLockRef = useRef<any>(null);
  const shouldBeActiveRef = useRef(false);

  /**
   * Request wake lock
   */
  const request = useCallback(async (): Promise<boolean> => {
    if (!isWakeLockSupported()) {
      setState((prev) => ({
        ...prev,
        error: "Wake Lock API not supported in this browser",
      }));
      return false;
    }

    try {
      shouldBeActiveRef.current = true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const wakeLock = await (navigator as any).wakeLock.request("screen");
      wakeLockRef.current = wakeLock;

      // Listen for release events (happens when tab loses visibility)
      wakeLock.addEventListener("release", () => {
        setState((prev) => ({
          ...prev,
          isActive: false,
        }));
      });

      setState({
        isSupported: true,
        isActive: true,
        error: null,
      });

      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to acquire wake lock";
      setState((prev) => ({
        ...prev,
        isActive: false,
        error: errorMessage,
      }));
      return false;
    }
  }, []);

  /**
   * Release wake lock
   */
  const release = useCallback(async (): Promise<void> => {
    shouldBeActiveRef.current = false;

    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      } catch {
        // Ignore release errors
      }
    }

    setState((prev) => ({
      ...prev,
      isActive: false,
      error: null,
    }));
  }, []);

  /**
   * Re-acquire wake lock when tab becomes visible
   */
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (
        document.visibilityState === "visible" &&
        shouldBeActiveRef.current &&
        !wakeLockRef.current
      ) {
        await request();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [request]);

  /**
   * Clean up on unmount
   */
  useEffect(() => {
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {
          // Ignore release errors on unmount
        });
      }
    };
  }, []);

  return {
    ...state,
    request,
    release,
  };
}
